import { useState, useRef, useEffect } from 'react';
import { api } from '../api';
import { chatSuggestions } from '../copy';
import { useTripBuilder } from '../TripBuilderContext';

const CHAT_KEY = 'pointspilot_advisor_chats';
function loadChats() {
  try { return JSON.parse(localStorage.getItem(CHAT_KEY)) || []; } catch { return []; }
}
function saveChats(chats) {
  try { localStorage.setItem(CHAT_KEY, JSON.stringify(chats)); } catch {}
}

export default function Advisor({ trip }) {
  const { amexTotal, chaseTotal } = useTripBuilder();
  const [messages, setMessages] = useState(() => loadChats());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0) saveChats(messages);
  }, [messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const { content } = await api.sendAdvisorMessage(newMessages);
      setMessages([...newMessages, { role: 'assistant', content }]);
    } catch {
      setMessages([...newMessages, { role: 'assistant', content: "Sorry, the advisor is unreachable right now. Check the backend connection and try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]">
      <div className="flex items-start gap-3 sm:gap-6 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal/20 flex items-center justify-center text-teal font-extrabold">AI</div>
            <div>
              <h1 className="text-2xl font-bold">Points Advisor</h1>
              <p className="text-brand-ink/70 text-sm mt-0.5">Ask anything about points, transfers, destinations, or strategy.</p>
            </div>
          </div>
          {messages.length > 0 && (
            <button onClick={() => { setMessages([]); saveChats([]); }} className="text-xs text-brand-ink/45 hover:text-brand-ink/65 transition mt-2">
              Clear chat
            </button>
          )}
        </div>
        {/* Context sidebar */}
        <div className="hidden lg:block w-64 space-y-3">
          <div className="bg-navy-light rounded-lg border border-brand-ink/10 p-3">
            <div className="text-xs text-brand-ink/65 font-semibold mb-2">YOUR POINTS</div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-brand-ink/75">Amex MR</span>
                <span className="font-semibold">{amexTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brand-ink/75">Chase UR</span>
                <span className="font-semibold">{chaseTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-brand-ink/10 pt-1 mt-1">
                <span className="text-brand-ink/75">Total</span>
                <span className="font-bold text-teal">{(amexTotal + chaseTotal).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="bg-navy-light rounded-lg border border-brand-ink/10 p-3">
            <div className="text-xs text-brand-ink/65 font-semibold mb-1">TRIP STATUS</div>
            <div className="text-sm capitalize">{trip?.status || 'Exploring'}</div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
        {messages.length === 0 && (
          <div className="space-y-4 pt-8">
            <div className="text-center text-brand-ink/65 text-sm mb-6">Ready when you are. What's on your mind?</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
              {chatSuggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setInput(s)}
                  className="animate-card-in text-left px-4 py-3 bg-navy-light border border-brand-ink/10 rounded-lg text-sm text-brand-ink/75 hover:text-white hover:border-brand-ink/20 hover:bg-navy-lighter transition"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} ${msg.role === 'user' ? 'animate-slide-in-right' : 'animate-slide-in-left'}`}>
            <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-teal/20 text-white'
                : 'bg-navy-light border border-brand-ink/10 text-brand-ink/85'
            }`}>
              <div className="whitespace-pre-wrap">{msg.content}</div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start animate-fade-in">
            <div className="bg-navy-light border border-brand-ink/10 rounded-xl px-4 py-4 flex items-center gap-1.5">
              <span className="typing-dot w-2 h-2 rounded-full bg-teal/70" />
              <span className="typing-dot w-2 h-2 rounded-full bg-teal/70" />
              <span className="typing-dot w-2 h-2 rounded-full bg-teal/70" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask about destinations, points, booking..."
          className="flex-1 bg-navy-lighter border border-brand-ink/10 rounded-xl px-4 py-3 text-sm text-white placeholder-brand-ink/40 focus:outline-none focus:border-teal"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="px-6 py-3 bg-teal text-navy font-bold rounded-xl text-sm hover:bg-teal-dark transition disabled:opacity-50"
        >
          Ask
        </button>
      </form>
    </div>
  );
}
